import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddCircle24Filled } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuDivider } from '@fluentui/react-components';
import { PostProjectLessonTag } from '../../redux/lessonSlice';
import styles from '../TaskManagement/css/index.module.css';

const LessonTagManagementMenu = ({ onTagSelect, children, lessonData }) => {
    const dispatch = useDispatch();
    const { lessonTags } = useSelector(
        state =>
            state.lesson || {
                lessonTags: [],
            },
    );

    const [newTagName, setNewTagName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);

    const handleMenuOpenChange = (e, data) => {
        setOpen(data.open);
    };

    const allTags = useMemo(() => {
        const tagMap = new Map();

        // 1. Project lesson tags from API
        if (lessonTags && lessonTags.length > 0) {
            lessonTags.forEach(tag => {
                if (tag?.name) {
                    tagMap.set(tag.name, { ...tag, source: 'project' });
                }
            });
        }

        // 2. Tags that already exist on lessons data
        if (Array.isArray(lessonData)) {
            lessonData
                .map(item => item?.tag)
                .filter(tag => tag && tag.trim() !== '')
                .forEach(tagName => {
                    if (!tagMap.has(tagName)) {
                        tagMap.set(tagName, {
                            id: `lesson_${tagName}`,
                            name: tagName,
                            source: 'lesson',
                        });
                    }
                });
        }

        return Array.from(tagMap.values());
    }, [lessonTags, lessonData]);

    const handleAddTag = async () => {
        if (!newTagName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectLessonTag({ name: newTagName.trim() }));
            setNewTagName('');
        } catch (error) {
            // Error toasts are handled in the slice
            // eslint-disable-next-line no-console
            console.error('Error adding lesson tag:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleTagClick = tag => {
        onTagSelect(tag);
        setOpen(false);
    };

    return (
        <Menu open={open} onOpenChange={handleMenuOpenChange}>
            <MenuTrigger>{children}</MenuTrigger>
            <MenuPopover style={{ width: '232px', maxHeight: '400px', overflowY: 'auto' }}>
                <MenuList>
                    {/* All Tags in one list */}
                    {allTags.length > 0 ? (
                        allTags.map(tag => (
                            <div key={tag.id} onClick={() => handleTagClick(tag)} className={styles.tagMenuItem}>
                                <span>{tag.name}</span>
                            </div>
                        ))
                    ) : (
                        <div>
                            <p>No tags available</p>
                        </div>
                    )}

                    {/* Add New Tag Section - Sticky at bottom */}
                    <div
                        style={{
                            position: 'sticky',
                            bottom: -5,
                            backgroundColor: 'white',
                            padding: '8px 12px',
                            // marginTop: '8px',
                        }}>
                        {/* <MenuDivider /> */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                placeholder="Add new tag"
                                className={styles.tagInput}
                                value={newTagName}
                                style={{ width: '170px' }}
                                onChange={e => setNewTagName(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                            />
                            <div>
                                <AddCircle24Filled
                                    color="var(--Brand-Secondary)"
                                    onClick={handleAddTag}
                                    disabled={!newTagName.trim() || isAdding}
                                    style={{ cursor: newTagName.trim() && !isAdding ? 'pointer' : 'default', fontSize: '20px' }}
                                />
                            </div>
                        </div>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default LessonTagManagementMenu;
